import { useParams, Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, Calendar, Share2, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import { POSTS, getPostBySlug } from "@/data/blog";
import { COMPANY } from "@/data/services";
import { SITE_URL } from "@/config/site";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  if (!post) return <Navigate to="/blog" replace />;

  const idx = POSTS.findIndex(p => p.slug === slug);
  const prev = POSTS[idx + 1];
  const next = POSTS[idx - 1];
  const related = POSTS.filter(p => p.slug !== slug).slice(0, 3);

  const url = `${SITE_URL}/blog/${post.slug}`;
  const shareLinks = [
    { name: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
    { name: "LinkedIn", url: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}` },
    { name: "WhatsApp", url: `https://api.whatsapp.com/send?text=${encodeURIComponent(post.title + " " + url)}` }
  ];

  return (
    <>
      <SEO
        title={`${post.metaTitle}`}
        description={post.metaDescription}
        canonical={url}
        image={post.image}
      />

      {/* Hero */}
      <article>
        <header className="bg-cream pt-12 lg:pt-16 pb-10 lg:pb-12 relative overflow-hidden">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12 relative">
            <nav className="text-xs text-forest/60 mb-8 font-outfit" aria-label="Fil d'Ariane">
              <Link to="/" className="hover:text-terracotta">Accueil</Link>
              <span className="mx-2">/</span>
              <Link to="/blog" className="hover:text-terracotta">Blog</Link>
              <span className="mx-2">/</span>
              <span className="text-forest">{post.title}</span>
            </nav>

            <div className="flex items-center gap-3 mb-5">
              <span className="font-outfit text-xs tracking-[0.2em] uppercase text-terracotta">{post.category}</span>
              <span className="text-forest/30">·</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-forest/60">
                <Calendar className="w-3 h-3" /> {post.dateLabel}
              </span>
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-forest leading-[1.05] tracking-tight text-balance">
              {post.title}
            </h1>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-12 pb-12">
          <img
            src={post.image}
            alt={post.title}
            className="w-full aspect-[16/9] object-cover rounded-3xl shadow-lg -mt-6 lg:-mt-8"
            loading="eager"
          />
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 lg:px-12 pb-16">
          <div className="prose-article" data-testid={`blog-content-${post.slug}`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-soft flex flex-wrap items-center gap-4">
            <span className="inline-flex items-center gap-2 text-forest/60 text-sm">
              <Share2 className="w-4 h-4" /> Partager :
            </span>
            {shareLinks.map(s => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-${s.name.toLowerCase()}`}
                className="px-4 py-2 bg-cream hover:bg-forest hover:text-white text-forest border border-soft rounded-full text-sm font-outfit transition-all"
              >
                {s.name}
              </a>
            ))}
          </div>

          {/* Prev / Next */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {prev ? (
              <Link to={`/blog/${prev.slug}`} data-testid="blog-prev" className="group p-5 bg-cream border border-soft rounded-2xl hover:border-terracotta transition-all">
                <span className="inline-flex items-center gap-1.5 text-xs text-forest/60 mb-1.5"><ArrowLeft className="w-3 h-3" /> Article précédent</span>
                <div className="font-outfit font-medium text-forest group-hover:text-terracotta transition-colors line-clamp-2">{prev.title}</div>
              </Link>
            ) : <div />}
            {next ? (
              <Link to={`/blog/${next.slug}`} data-testid="blog-next" className="group p-5 bg-cream border border-soft rounded-2xl hover:border-terracotta transition-all text-right">
                <span className="inline-flex items-center justify-end gap-1.5 text-xs text-forest/60 mb-1.5 w-full">Article suivant <ArrowRight className="w-3 h-3" /></span>
                <div className="font-outfit font-medium text-forest group-hover:text-terracotta transition-colors line-clamp-2">{next.title}</div>
              </Link>
            ) : <div />}
          </div>
        </div>
      </article>

      {/* CTA form */}
      <section className="py-16 lg:py-24 bg-forest text-white relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 relative">
          <div>
            <div className="font-outfit text-xs tracking-[0.3em] uppercase text-terracotta mb-4">Devis gratuit</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight mb-6">
              Besoin d'un conseil sur votre assurance ?
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-8">
              Notre équipe analyse votre activité et vous propose une couverture adaptée, en métropole comme dans les DOM-TOM.
            </p>
            <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-2 text-white hover:text-terracotta">
              <Phone className="w-4 h-4" /> {COMPANY.phoneDisplay}
            </a>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-3xl shadow-2xl">
            <ContactForm defaultService="Assurance Décennale" source={`blog-${post.slug}`} />
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16 lg:py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
          <div className="flex items-end justify-between mb-10">
            <h2 className="font-display text-3xl sm:text-4xl text-forest">Articles à découvrir</h2>
            <Link to="/blog" className="text-terracotta font-outfit font-medium inline-flex items-center gap-1.5">
              Tout le blog <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map(r => (
              <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`related-${r.slug}`} className="group">
                <div className="overflow-hidden rounded-2xl aspect-[4/3] mb-4 bg-white">
                  <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <span className="font-outfit text-[10px] tracking-[0.2em] uppercase text-terracotta">{r.category}</span>
                <h3 className="font-display text-lg text-forest mt-2 group-hover:text-terracotta transition-colors line-clamp-2">{r.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
